import { authFetch } from './auth';

const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8100/api' : '/api';

// Client-side limits (the server enforces the same ones: docs/schema-v2.md §5)
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_FILES_PER_SHEET = 10;
export const MAX_LABELS = 4; // label photos per wine, beer or product
export const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

// Owners of attachments: sheets (any role) or the wine, beer and product catalogs (label photos only).
export const isCatalogOwner = (ownerType) => ownerType === 'wines' || ownerType === 'beers' || ownerType === 'products';
export const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const ALLOWED_TYPES = ACCEPT.split(',');

// Returns an i18n error key ('attachments.error.too_many', ...) or null when the files can be added.
export function validateNewFiles(existingCount, files, ownerType = 'sheets') {
  const catalog = isCatalogOwner(ownerType);
  if (existingCount + files.length > (catalog ? MAX_LABELS : MAX_FILES_PER_SHEET)) {
    return catalog ? 'attachments.error.too_many_labels' : 'attachments.error.too_many';
  }
  for (const f of files) {
    if (!(catalog ? IMAGE_ACCEPT.split(',') : ALLOWED_TYPES).includes(f.type)) return 'attachments.error.bad_type';
    if (f.size > MAX_FILE_BYTES) return 'attachments.error.too_large';
  }
  return null;
}

// Uploads the files one by one. items: [{ file, role, caption }].
// Returns { uploaded: n, failed: [{ name, code, detail }] }; `code`/`detail` come from the API error (detail is already localized).
export async function uploadAttachments(ownerId, items, ownerType = 'sheets') {
  let uploaded = 0;
  const failed = [];
  for (const item of items) {
    const body = new FormData();
    body.append('file', item.file);
    if (!isCatalogOwner(ownerType)) body.append('role', item.role || 'photo'); // catalog files are always labels
    if (item.caption) body.append('caption', item.caption);
    try {
      const res = await authFetch(`${API_URL}/${ownerType}/${ownerId}/attachments`, { method: 'POST', body });
      if (!res || !res.ok) {
        const error = res ? await res.json().catch(() => ({})) : {};
        failed.push({ name: item.file.name, code: error.code || null, detail: error.detail || null });
        continue;
      }
      uploaded += 1;
    } catch {
      failed.push({ name: item.file.name, code: null, detail: null });
    }
  }
  return { uploaded, failed };
}

export async function listAttachments(ownerId, ownerType = 'sheets') {
  const res = await authFetch(`${API_URL}/${ownerType}/${ownerId}/attachments`);
  return res && res.ok ? res.json() : [];
}

// Files of private sheets need the Authorization header, which <img src> cannot send: fetch them as blobs.
export async function fetchAttachmentBlobUrl(id, kind = 'file') {
  const res = await authFetch(`${API_URL}/attachments/${id}/${kind}`);
  if (!res || !res.ok) return null;
  return URL.createObjectURL(await res.blob());
}

// Id of the signed-in user (cached), used to know which catalog files the user uploaded; null when anonymous.
let currentUserId;
export async function getCurrentUserId() {
  if (currentUserId !== undefined) return currentUserId;
  try {
    const res = await authFetch(`${API_URL}/me`);
    currentUserId = res && res.ok ? (await res.json()).id : null;
  } catch {
    currentUserId = null;
  }
  return currentUserId;
}

export async function deleteAttachment(id) {
  const res = await authFetch(`${API_URL}/attachments/${id}`, { method: 'DELETE' });
  return !!(res && res.ok);
}

// Text for a failed upload: the localized API message when there is one, else the generic one (t = i18n function).
export function describeUploadFailure(failure, t) {
  if (failure && failure.code) {
    const text = t(`api.${failure.code}`);
    if (text !== `api.${failure.code}`) return text;
  }
  return (failure && failure.detail) || t('attachments.error.upload_failed');
}
